/**
 * Single ribbon hint line for InboundInterchange — one muted line beside
 * the action buttons that names the FIRST unmet step, a positive state
 * when the next action is ready, or the blocking reason after a failed
 * assessment. Pure: vitest-covered, mirrors the disabled expressions in
 * the ribbon buttons.
 *
 * Vocabulary matches the buttons + Dispatch Gateway: 'assessment',
 * 'pass', 'cut', 'dispatch the work order'.
 */

export interface RibbonHintInput {
  hasManifest: boolean
  pendingCount: number
  processedCount: number
  isCutActive: boolean
  cutIndex: number | null
  hasDestinationTrack: boolean
  hasLocomotive: boolean
  isRunningAssessment: boolean
  hasAssessment: boolean
  assessmentBlocked: boolean
  /** Post-dispatch: the work order went out and the drawer is open. */
  dispatched: boolean
}

export type RibbonHintTone = 'muted' | 'positive' | 'blocked'

export function ribbonHint(input: RibbonHintInput): { text: string; tone: RibbonHintTone } {
  const {
    hasManifest,
    pendingCount,
    processedCount,
    isCutActive,
    cutIndex,
    hasDestinationTrack,
    hasLocomotive,
    isRunningAssessment,
    hasAssessment,
    assessmentBlocked,
    dispatched,
  } = input

  if (!hasManifest) {
    return { text: 'Select a manifest to begin', tone: 'muted' }
  }
  if (dispatched) {
    return { text: 'Work order dispatched — switch list ready', tone: 'positive' }
  }
  if (isRunningAssessment) {
    return { text: 'Assessment in progress…', tone: 'muted' }
  }
  if (pendingCount === 0 && processedCount === 0) {
    return { text: 'No pending cars on this manifest', tone: 'muted' }
  }
  if (isCutActive && cutIndex === null) {
    return { text: 'Choose where to cut', tone: 'muted' }
  }
  if (isCutActive) {
    return { text: 'Confirm the placed pass — or pick a different seam', tone: 'muted' }
  }
  if (processedCount > 0 && pendingCount === 0) {
    return { text: 'All cars placed — dispatch the work order', tone: 'positive' }
  }
  if (processedCount > 0) {
    return { text: 'Next pass — cut in the manifest or confirm the rest', tone: 'muted' }
  }
  if (!hasDestinationTrack) {
    return { text: 'Select a destination track', tone: 'muted' }
  }
  if (!hasLocomotive) {
    return { text: 'Select a locomotive', tone: 'muted' }
  }
  if (assessmentBlocked) {
    return { text: 'Assessment has blocking findings — see Safety Alerts', tone: 'blocked' }
  }
  if (!hasAssessment) {
    return { text: 'Run the predictive assessment', tone: 'muted' }
  }
  return { text: 'Assessment clear — dispatch the work order', tone: 'positive' }
}
